import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axios-instance";

const utcToday = () => new Date().toISOString().slice(0, 10);

const utcDaysAgo = (days) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
};

const formatRate = (rate) => {
  if (rate === null || rate === undefined) return "—";
  return `${(Number(rate) * 100).toFixed(1)}%`;
};

const formatHours = (hours) => {
  if (hours === null || hours === undefined) return "—";
  return `${Number(hours).toFixed(1)}h`;
};

const formatNumber = (n) => {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString();
};

const formatMoney = (n) => {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/**
 * Ticket 20 — minimal SuperAdmin Launch Analytics dashboard.
 * Data from GET /api/super-admin/launch-analytics (Ticket 19).
 */
const LaunchAnalytics = () => {
  const [from, setFrom] = useState(utcDaysAgo(6));
  const [to, setTo] = useState(utcToday());
  const [utmSource, setUtmSource] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReport = useCallback((params) => {
    setLoading(true);
    setError(null);

    const qs = new URLSearchParams();
    qs.set("from", params.from);
    qs.set("to", params.to);
    if (params.utmSource) qs.set("utm_source", params.utmSource);
    if (params.utmCampaign) qs.set("utm_campaign", params.utmCampaign);

    axiosInstance
      .get(`super-admin/launch-analytics?${qs.toString()}`)
      .then((res) => setData(res.data))
      .catch((err) => {
        setData(null);
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to load launch analytics."
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchReport({ from, to, utmSource: "", utmCampaign: "" });
    // Initial load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchReport({
      from,
      to,
      utmSource: utmSource.trim(),
      utmCampaign: utmCampaign.trim(),
    });
  };

  const downloadAcquisitionCsv = () => {
    const rows = data?.acquisition?.by_dimension || [];
    const header = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "email_creative_id",
      "signups",
    ];
    const lines = [header.join(",")];
    rows.forEach((r) => {
      lines.push(
        [
          r.utm_source,
          r.utm_medium,
          r.utm_campaign,
          r.utm_content,
          r.email_creative_id,
          r.signups,
        ]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `launch-analytics-acquisition-${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activation = data?.activation;
  const ttv = data?.ttv;
  const cohorts = data?.cohorts;
  const retention = data?.retention;
  const revenue = data?.revenue;
  const reliability = data?.reliability;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Launch Analytics</h1>
          <p style={styles.subtitle}>
            Acquisition → activation → bookings → subscriptions (UTC). Null
            attribution shows as “Direct / unknown”.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={styles.filters}>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>From</span>
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            style={styles.input}
          />
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>To</span>
          <input
            type="date"
            value={to}
            max={utcToday()}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            style={styles.input}
          />
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>UTM source</span>
          <input
            type="text"
            value={utmSource}
            onChange={(e) => setUtmSource(e.target.value)}
            placeholder="optional"
            style={styles.input}
          />
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>UTM campaign</span>
          <input
            type="text"
            value={utmCampaign}
            onChange={(e) => setUtmCampaign(e.target.value)}
            placeholder="optional"
            style={styles.input}
          />
        </label>
        <button type="submit" style={styles.searchBtn} disabled={loading}>
          {loading ? "Loading…" : "Apply"}
        </button>
      </form>

      {error && <div style={styles.errorBox}>{error}</div>}

      {loading && (
        <div style={styles.loadingBox}>
          <div style={styles.spinner} />
          <span>
            Loading analytics for {from} → {to}…
          </span>
        </div>
      )}

      {data && !loading && (
        <>
          <div style={styles.metaBanner}>
            Cohort businesses:{" "}
            <strong>{formatNumber(data.meta?.cohort_business_count)}</strong>
            &nbsp;|&nbsp; Touch: {data.meta?.attribution_touch || "signup"}
            &nbsp;|&nbsp; CAC deferred
            {data.meta?.cac_note ? ` (${data.meta.cac_note})` : ""}
          </div>

          {/* KPI cards */}
          <div style={styles.grid}>
            <KpiCard
              label="Signups"
              value={formatNumber(activation?.signups)}
              hint="user_signed_up in range"
            />
            <KpiCard
              label="Calendars connected"
              value={formatNumber(activation?.funnel_events?.calendar_connected)}
              hint="in cohort"
            />
            <KpiCard
              label="First bookings"
              value={formatNumber(activation?.funnel_events?.first_booking_received)}
              hint="in cohort"
            />
            <KpiCard
              label="Subscriptions started"
              value={formatNumber(
                revenue?.saas?.new_subscription_starts_in_range
              )}
              hint="in range"
            />
            <KpiCard
              label="Setup rate"
              value={formatRate(activation?.setup_rate)}
              hint={`${formatNumber(activation?.setup_activated)} setup activated`}
            />
            <KpiCard
              label="Product rate"
              value={formatRate(activation?.product_rate)}
              hint={`${formatNumber(activation?.product_activated)} product activated`}
            />
            <KpiCard
              label="TTV → calendar"
              value={formatHours(ttv?.median_hours_to_calendar)}
              hint={`n=${formatNumber(ttv?.sample_sizes?.calendar)}`}
            />
            <KpiCard
              label="TTV → first booking"
              value={formatHours(ttv?.median_hours_to_first_booking)}
              hint={`n=${formatNumber(ttv?.sample_sizes?.first_booking)}`}
            />
            <KpiCard
              label="SaaS MRR (cohort)"
              value={formatMoney(revenue?.saas?.mrr)}
              hint={`${formatNumber(revenue?.saas?.active_paid_subscriptions)} paid`}
            />
            <KpiCard
              label="Paid booking GMV"
              value={formatMoney(revenue?.paid_bookings_gmv?.gmv_net_after_discount)}
              hint={`${formatNumber(revenue?.paid_bookings_gmv?.booking_count)} bookings (not MRR)`}
            />
            <KpiCard
              label="Early retained"
              value={formatRate(retention?.early_retained_rate)}
              hint={`${formatNumber(retention?.early_retained)} / ${retention?.window_days || 7}d window`}
            />
            <KpiCard
              label="D7 product cohort"
              value={formatRate(cohorts?.d7?.rate)}
              hint={`${formatNumber(cohorts?.d7?.activated_product)} / ${formatNumber(cohorts?.d7?.eligible)} eligible`}
            />
          </div>

          {/* Cohorts table */}
          <Section title="Activation cohorts (UTC calendar days)">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Cohort</th>
                  <th style={styles.th}>Eligible</th>
                  <th style={styles.th}>Product activated</th>
                  <th style={styles.th}>Rate</th>
                </tr>
              </thead>
              <tbody>
                {["d1", "d3", "d7"].map((key) => (
                  <tr key={key}>
                    <td style={styles.td}>{key.toUpperCase()}</td>
                    <td style={styles.td}>{formatNumber(cohorts?.[key]?.eligible)}</td>
                    <td style={styles.td}>
                      {formatNumber(cohorts?.[key]?.activated_product)}
                    </td>
                    <td style={styles.td}>{formatRate(cohorts?.[key]?.rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={styles.note}>
              Semantics: {cohorts?.semantics || "utc_calendar_days"}. As of{" "}
              {cohorts?.as_of || data.meta?.to}.
            </p>
          </Section>

          {/* Acquisition */}
          <Section
            title="Acquisition by campaign / source"
            action={
              (data.acquisition?.by_dimension || []).length > 0 ? (
                <button type="button" style={styles.secondaryBtn} onClick={downloadAcquisitionCsv}>
                  Export CSV
                </button>
              ) : null
            }
          >
            <div style={styles.totalsRow}>
              Signups: <strong>{formatNumber(data.acquisition?.totals?.signups)}</strong>
              &nbsp;|&nbsp; With attribution:{" "}
              <strong>{formatNumber(data.acquisition?.totals?.with_attribution)}</strong>
              &nbsp;|&nbsp; Direct / unknown:{" "}
              <strong>{formatNumber(data.acquisition?.totals?.direct_unknown)}</strong>
            </div>
            {(data.acquisition?.by_dimension || []).length === 0 ? (
              <p style={styles.empty}>No signups in this range.</p>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Source</th>
                      <th style={styles.th}>Medium</th>
                      <th style={styles.th}>Campaign</th>
                      <th style={styles.th}>Content</th>
                      <th style={styles.th}>Creative</th>
                      <th style={styles.th}>Signups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.acquisition.by_dimension.map((row, idx) => (
                      <tr key={idx}>
                        <td style={styles.td}>{row.utm_source}</td>
                        <td style={styles.td}>{row.utm_medium}</td>
                        <td style={styles.td}>{row.utm_campaign}</td>
                        <td style={styles.td}>{row.utm_content}</td>
                        <td style={styles.td}>{row.email_creative_id}</td>
                        <td style={styles.td}>{formatNumber(row.signups)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Funnel */}
          <Section title="Activation funnel (cohort event presence)">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Event</th>
                  <th style={styles.th}>Businesses</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(activation?.funnel_events || {}).map(([name, count]) => (
                  <tr key={name}>
                    <td style={styles.td}>
                      <code style={styles.code}>{name}</code>
                    </td>
                    <td style={styles.td}>{formatNumber(count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* Reliability */}
          {reliability?.available && (
            <Section title="Reliability (platform-wide, optional)">
              {(reliability.by_flow || []).length === 0 ? (
                <p style={styles.empty}>No operational log rows in range.</p>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Flow</th>
                      <th style={styles.th}>Errors</th>
                      <th style={styles.th}>Warnings</th>
                      <th style={styles.th}>Other</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reliability.by_flow.map((row) => (
                      <tr key={row.flow}>
                        <td style={styles.td}>{row.flow}</td>
                        <td style={styles.td}>{formatNumber(row.error_count)}</td>
                        <td style={styles.td}>{formatNumber(row.warning_count)}</td>
                        <td style={styles.td}>{formatNumber(row.other_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {reliability.note && <p style={styles.note}>{reliability.note}</p>}
            </Section>
          )}
        </>
      )}
    </div>
  );
};

const KpiCard = ({ label, value, hint }) => (
  <div style={styles.card}>
    <div style={styles.cardLabel}>{label}</div>
    <div style={styles.cardValue}>{value}</div>
    {hint && <div style={styles.cardHint}>{hint}</div>}
  </div>
);

const Section = ({ title, children, action }) => (
  <section style={styles.section}>
    <div style={styles.sectionHead}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const styles = {
  page: {
    padding: "32px 24px",
    maxWidth: 1100,
    margin: "0 auto",
    fontFamily: "inherit",
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    margin: 0,
    color: "#111827",
  },
  subtitle: {
    margin: "4px 0 0",
    color: "#6b7280",
    fontSize: 14,
    maxWidth: 640,
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "flex-end",
    marginBottom: 24,
    padding: 16,
    background: "#f9fafb",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
  },
  input: {
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "7px 12px",
    fontSize: 14,
    color: "#111827",
    outline: "none",
    minWidth: 140,
  },
  searchBtn: {
    background: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "8px 18px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    height: 36,
  },
  secondaryBtn: {
    background: "#fff",
    color: "#4f46e5",
    border: "1px solid #c7d2fe",
    borderRadius: 6,
    padding: "6px 12px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#b91c1c",
    border: "1px solid #fca5a5",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 20,
    fontSize: 14,
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    color: "#6b7280",
    padding: "32px 0",
    fontSize: 15,
  },
  spinner: {
    width: 22,
    height: 22,
    border: "3px solid #e5e7eb",
    borderTop: "3px solid #4f46e5",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  metaBanner: {
    background: "#eef2ff",
    border: "1px solid #c7d2fe",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 20,
    fontSize: 13,
    color: "#3730a3",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 14,
    marginBottom: 28,
  },
  card: {
    background: "#fff",
    border: "1.5px solid #e5e7eb",
    borderRadius: 10,
    padding: "16px 16px 12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  cardLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    marginBottom: 6,
  },
  cardValue: {
    fontSize: 22,
    fontWeight: 700,
    color: "#111827",
  },
  cardHint: {
    marginTop: 4,
    fontSize: 11,
    color: "#9ca3af",
  },
  section: {
    marginBottom: 28,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 18,
  },
  sectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    gap: 12,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "2px solid #e5e7eb",
    color: "#6b7280",
    fontWeight: 600,
    fontSize: 12,
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #f3f4f6",
    color: "#111827",
    verticalAlign: "top",
  },
  code: {
    fontSize: 12,
    background: "#f3f4f6",
    padding: "2px 6px",
    borderRadius: 4,
  },
  totalsRow: {
    fontSize: 13,
    color: "#374151",
    marginBottom: 12,
  },
  note: {
    margin: "10px 0 0",
    fontSize: 12,
    color: "#6b7280",
  },
  empty: {
    margin: 0,
    fontSize: 14,
    color: "#9ca3af",
  },
};

export default LaunchAnalytics;
